
import { API_URL } from "../../constants";
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const studentInitialState = {
  // List
  students: [],
  studentsLoading: false,
  studentsError: null,
  studentsTotal: 0,
  studentsPages: 1,

  // Single student
  studentDetail: null,
  studentDetailLoading: false,
  studentDetailError: null,

  // Student purchases
  studentPurchases: [],
  studentPurchasesLoading: false,
  studentPurchasesError: null,

  // Student subscriptions
  studentSubscriptions: [],
  studentSubscriptionsLoading: false,
  studentSubscriptionsError: null,

  // Student learning progress
  studentProgress: null,
  studentProgressLoading: false,
  studentProgressError: null,

  // Status update
  statusUpdateLoading: false,
  statusUpdateError: null,
};

/**
 * Normalize the varying shapes the `/api/admin/students` list endpoint may
 * return into `{ items, total, totalPages }`.
 */
function normalizeListPayload(data) {
  if (!data || typeof data !== "object") {
    return { items: [], total: 0, totalPages: 1 };
  }

  // The list endpoint returns `{ data: [...], pagination: { total, totalPages } }`.
  // `data` may be an array of students directly, or wrapped in an object.
  let items = [];
  if (Array.isArray(data.data)) {
    items = data.data;
  } else if (Array.isArray(data)) {
    items = data;
  } else {
    const payload =
      data.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data;
    items = payload.items || data.items || payload.students || data.students || [];
  }

  const pagination = data.pagination || {};
  const total =
    pagination.total ??
    data.total ??
    (Array.isArray(items) ? items.length : 0) ??
    0;
  const totalPages =
    pagination.totalPages ??
    pagination.pages ??
    data.totalPages ??
    data.pages ??
    1;

  return {
    items: Array.isArray(items) ? items : [],
    total,
    totalPages,
  };
}

/**
 * Map the real `/api/admin/students` resource shape into the stable shape the
 * admin UI renders (name, isActive, dateAdded, isVerified, phoneNumber, ...).
 * The list endpoint returns `fullName`, `accountStatus`, `emailVerified` and
 * `createdAt` for each student — the UI should display those last in the list.
 */
function normalizeStudent(raw) {
  if (!raw || typeof raw !== "object") return raw || {};
  const status = raw.accountStatus || raw.status;
  const isActive =
    typeof raw.isActive === "boolean"
      ? raw.isActive
      : status !== undefined && status !== null
        ? String(status).toUpperCase() !== "SUSPENDED" && String(status).toUpperCase() !== "INACTIVE" && String(status).toUpperCase() !== "DISABLED"
        : false;
  return {
    ...raw,
    name: raw.fullName || raw.name || raw.full_name || `${raw.first_name || ""} ${raw.last_name || ""}`.trim() || "—",
    fullName: raw.fullName || raw.name || `${raw.first_name || ""} ${raw.last_name || ""}`.trim(),
    isActive: isActive,
    status: status || (isActive ? "ACTIVE" : "SUSPENDED"),
    accountStatus: status || (isActive ? "ACTIVE" : "SUSPENDED"),
    emailVerified: raw.emailVerified ?? raw.isEmailVerified ?? false,
    phoneNumber: raw.phoneNumber ?? raw.phone ?? null,
    dateAdded: raw.dateAdded || raw.createdAt,
    createdAt: raw.createdAt || raw.dateAdded,
    lastActive: raw.lastActive || raw.updatedAt || raw.createdAt || raw.dateAdded,
  };
}

export function createStudentActions(set, get) {
  return {
    /**
     * GET /api/admin/students?{page,limit,search,status,emailVerified,...}
     * Returns `{ success, students, total, pages }`.
     */
    fetchStudents: async (params = {}) => {
      set({ studentsLoading: true, studentsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ""
          )
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/students${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (!response.ok || data.error) {
          const msg = toDisplayString(data.error || data.message, "Failed to fetch students");
          set({ studentsLoading: false, studentsError: msg });
          return { success: false, error: msg };
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        const normalizedItems = items.map(normalizeStudent);
        set({
          studentsLoading: false,
          students: normalizedItems,
          studentsTotal: total,
          studentsPages: totalPages,
          studentsError: null,
        });
        return { success: true, students: normalizedItems, total, pages: totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch students");
        set({ studentsLoading: false, studentsError: msg });
        return { success: false, error: msg };
      }
    },

    /**
     * GET /api/admin/students/:id
     * Returns `{ success, student }`.
     */
    fetchStudent: async (id) => {
      set({ studentDetailLoading: true, studentDetailError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/students/${id}`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const msg = toDisplayString(data.error || data.message, "Failed to fetch student");
          set({ studentDetailLoading: false, studentDetailError: msg });
          return { success: false, error: msg };
        }
        const student = normalizeStudent(data.student || data.data || data);
        set({ studentDetailLoading: false, studentDetail: student, studentDetailError: null });
        return { success: true, student };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch student");
        set({ studentDetailLoading: false, studentDetailError: msg });
        return { success: false, error: msg };
      }
    },
        /**
     * GET /api/admin/students/:id/purchases
     * Returns `{ success, purchases }`.
     */
    fetchStudentPurchases: async (id, params = {}) => {
      set({ studentPurchasesLoading: true, studentPurchasesError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ""
          )
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/students/${id}/purchases${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (!response.ok || data.error) {
          const msg = toDisplayString(data.error || data.message, "Failed to fetch purchases");
          set({ studentPurchasesLoading: false, studentPurchasesError: msg });
          return { success: false, error: msg };
        }
        const purchases = data.purchases || data.data || data.items || data || [];
        set({
          studentPurchasesLoading: false,
          studentPurchases: Array.isArray(purchases) ? purchases : [],
          studentPurchasesError: null,
        });
        return { success: true, purchases };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch purchases");
        set({ studentPurchasesLoading: false, studentPurchasesError: msg });
        return { success: false, error: msg };
      }
    },

    /**
     * GET /api/admin/students/:id/subscriptions
     * Returns `{ success, subscriptions }`.
     */
    fetchStudentSubscriptions: async (id, params = {}) => {
      set({ studentSubscriptionsLoading: true, studentSubscriptionsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ""
          )
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/students/${id}/subscriptions${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (!response.ok || data.error) {
          const msg = toDisplayString(data.error || data.message, "Failed to fetch subscriptions");
          set({ studentSubscriptionsLoading: false, studentSubscriptionsError: msg });
          return { success: false, error: msg };
        }
        const subscriptions =
          data.subscriptions || data.data || data.items || data || [];
        set({
          studentSubscriptionsLoading: false,
          studentSubscriptions: Array.isArray(subscriptions) ? subscriptions : [],
          studentSubscriptionsError: null,
        });
        return { success: true, subscriptions };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch subscriptions");
        set({ studentSubscriptionsLoading: false, studentSubscriptionsError: msg });
        return { success: false, error: msg };
      }
    },

    /**
     * GET /api/admin/students/:id/learning-progress
     * Returns `{ success, progress }`.
     */
    fetchStudentLearningProgress: async (id) => {
      set({ studentProgressLoading: true, studentProgressError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/students/${id}/learning-progress`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const msg = toDisplayString(data.error || data.message, "Failed to fetch learning progress");
          set({ studentProgressLoading: false, studentProgressError: msg });
          return { success: false, error: msg };
        }
        const progress = data.progress || data.learningProgress || data.data || data;
        set({ studentProgressLoading: false, studentProgress: progress, studentProgressError: null });
        return { success: true, progress };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch learning progress");
        set({ studentProgressLoading: false, studentProgressError: msg });
        return { success: false, error: msg };
      }
    },

    /**
     * PATCH /api/admin/students/:id/status
     * Toggles a student's active status. Pass the desired status explicitly
     * (e.g. `"SUSPENDED"` or `"ACTIVE"`) — the endpoint is a PATCH so we send
     * the new status in the body.
     * Returns `{ success }`.
     */
    updateStudentStatus: async (id, status) => {
      set({ statusUpdateLoading: true, statusUpdateError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/students/${id}/status`, {
          method: "PATCH",
          headers: authHeaders(resolveToken(get)),
          body: JSON.stringify({ status }),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const msg = toDisplayString(data.error || data.message, "Failed to update student status");
          set({ statusUpdateLoading: false, statusUpdateError: msg });
          return { success: false, error: msg };
        }
        // Update the student locally so the UI reflects the new status immediately
        // without a full refetch (which can cause a "No students found" flash).
        const updatedStudents = get().students.map((s) =>
          String(s.id) === String(id)
            ? {
                ...s,
                isActive: String(status).toUpperCase() === "ACTIVE",
                status,
                accountStatus: status,
              }
            : s
        );
        set({
          statusUpdateLoading: false,
          statusUpdateError: null,
          students: updatedStudents,
          successMessage: "Student status updated successfully",
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to update student status");
        set({ statusUpdateLoading: false, statusUpdateError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const studentActions = createStudentActions;
export default studentActions;